import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoClose, IoLocationOutline } from 'react-icons/io5';

const ProfileCard = ({ 
  user, 
  onClose,
  style = {}
}) => {
  if (!user) {
    return (
      <div className="bg-red-900 p-4 rounded-lg text-white">
        Error: No user data provided
      </div>
    );
  }

  const {
    id,
    name,
    jobTitle,
    profilePhotoUrl,
    location
  } = user;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const handleCloseClick = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      transition={{ type: "spring", damping: 20 }}
      className="relative overflow-hidden rounded-xl shadow-2xl w-full h-[400px] bg-pri-dark"
      style={{
        ...style,
        minWidth: '240px',
        minHeight: '340px',
        border: '2px solid rgba(255, 255, 255, 0.2)'
      }}
    >
      {/* Full-screen background image with gradient overlay */}
      <div className="absolute inset-0">
        {profilePhotoUrl ? (
          <>
            <img
              src={profilePhotoUrl}
              alt={name || 'User profile'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {/* Gradient overlay that stretches across the entire height for seamless transition */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" 
                 style={{
                   background: 'linear-gradient(to top, rgba(0,0,0,1) 10%, rgba(0,0,0,0.85) 20%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 80%)'
                 }}
            />
          </>
        ) : (
          <>
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-700 to-blue-900">
              <span className="text-white font-bold text-6xl">{getInitials(name || 'User')}</span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" 
                 style={{
                   background: 'linear-gradient(to top, rgba(0,0,0,1) 10%, rgba(0,0,0,0.85) 20%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 80%)'
                 }}
            />
          </>
        )}
      </div>

      {/* Close button */}
      {onClose && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleCloseClick}
          className="absolute top-2 right-2 z-[110] p-2 rounded-full bg-black/80 backdrop-blur-sm text-white hover:bg-black/90"
        >
          <IoClose className="h-5 w-5" />
        </motion.button>
      )}

      {/* Content overlay at bottom 25% */}
      <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col items-center z-[101]">
        {/* User Name */}
        <h2 className="text-xl font-bold text-soft-pink text-center drop-shadow-md">{name || 'Unknown User'}</h2>
        
        {/* Job Title */}
        {jobTitle && (
          <p className="text-soft-pink/80 text-sm mt-1 text-center drop-shadow-md">{jobTitle}</p>
        )}
        
        {/* Location only */}
        {location && (
          <p className="text-soft-pink/70 text-xs flex items-center justify-center mt-1">
            <IoLocationOutline className="mr-1" /> {location}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ProfileCard; 